import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SimpleArrowAccordionV2 } from 'src/components/Accordion/SimpleArrowAccordionV2/SimpleArrowAccordionV2';
import { AccordionItem } from 'src/components/Accordion/models';
import { useCart } from 'src/components/Cart/UseCart';
import { useFetchingPromoContext } from 'src/components/OrderSummary/PromoSummary/FetchingPromoContext/FetchingPromoContext';
import { AppliedPromo } from './AppliedPromo/AppliedPromo';
import { LoadingPromo } from './LoadingPromo/LoadingPromo';
import { PromoCodeInput } from './PromoCodeInput/PromoCodeInput';

import styles from './PromoSummary.scss';

import { useValidatedProgramPromotion } from 'src/components/ProgramPromotion/ValidatedProgramPromotionContext';

interface PromoSummaryProps {
  promoBoxVisible?: boolean;
}

export const PromoSummary: React.FC<PromoSummaryProps> = ({ promoBoxVisible = true }) => {
  const { addPromo, checkout: { promo : promoInCart } } = useCart();
  const { programPromotion, isProgramPromoLoading } = useValidatedProgramPromotion();
  const { t } = useTranslation();
  const { fetchingPromo } = useFetchingPromoContext();
  const [accordionState, setAccordionState] = useState(true);

  const promotionLoading = fetchingPromo.status === 'loading' || isProgramPromoLoading;

  useEffect(() => {
    if (!programPromotion) return;

    addPromo({
      program: programPromotion.promoCode,
    });
    setAccordionState(false);
  }, [programPromotion]);

  useEffect(() => {
    if (fetchingPromo.status !== 'success') return;

    addPromo({
      applied: fetchingPromo.data.promoCode,
    });
    setAccordionState(false);
  }, [fetchingPromo]);

  useEffect(() => {
    if (promoInCart.program !== null) {
      setAccordionState(false);
    }
  }, [promoInCart.program]);

  return (
    <section
      className={styles.promoSummary}
      aria-live="polite"
      role="status"
    >
      {promoBoxVisible && (
        <SimpleArrowAccordionV2
          className={styles.promoSummaryAccordion}
          items={[{
            defaultOpen: accordionState,
            headerClassName: styles.promoSummaryAccordionHeader,
            title: t('promo-code.heading'),
            body: <PromoCodeInput handleProgramPromo={() => setAccordionState(!accordionState)} />,
          } as AccordionItem]}
          data-test="promo-accordion"
        />
      )}

      {promotionLoading && <LoadingPromo />}

      {!promotionLoading && promoInCart.applied && (
        <AppliedPromo handleRemovePromo={() => setAccordionState(!accordionState)} disabledXButton={!promoBoxVisible} />
      )}
    </section>
  );
};

PromoSummary.displayName = 'PromoSummary';
